import React, { useEffect, useState, useRef } from "react";
import "../../Styles/Pages/Profile.css";
import Topbar from "../../Layouts/Admin_layout/Admin_topbar";
import {
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Card,
  CardContent,
  Box,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import instance from "../../utils/api";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiEdit,
  FiPlus,
  FiTrash2,
  FiPhone,
  FiMapPin,
  FiMail,
  FiUser,
  FiUploadCloud,
} from "react-icons/fi";

function Profile() {
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openEdit, setOpenEdit] = useState(false);
  
  // Edit Form State
  const [editName, setEditName] = useState("");
  const [editMobile, setEditMobile] = useState("");
  const [editState, setEditState] = useState("");
  const [alternateMobiles, setAlternateMobiles] = useState([]);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);

  const navigate = useNavigate();

  const fetchVendorDetails = async () => {
    try {
      setLoading(true);
      const response = await instance.get("/vendor/getVendorDetails");
      if (response.data && response.data.vendor) {
        setVendor(response.data.vendor);
      }
    } catch (err) {
      console.error("Error fetching vendor details:", err);
      toast.error("Failed to fetch profile details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendorDetails();
  }, []);

  const handleOpenEdit = () => {
    if (vendor) {
      setEditName(vendor.name || "");
      setEditMobile(vendor.mobile_no || "");
      setEditState(vendor.state || "");
      setAlternateMobiles(vendor.alternate_mobile_numbers || []);
      setLogoFile(null);
      setLogoPreview(vendor.company_logo_url || null);
      setOpenEdit(true);
    }
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleAddAltMobile = () => {
    if (alternateMobiles.length < 4) {
      setAlternateMobiles([...alternateMobiles, ""]);
    }
  };

  const handleAltMobileChange = (index, value) => {
    if (/^\d*$/.test(value) && value.length <= 10) {
      const updated = [...alternateMobiles];
      updated[index] = value;
      setAlternateMobiles(updated);
    }
  };

  const handleRemoveAltMobile = (index) => {
    const updated = alternateMobiles.filter((_, i) => i !== index);
    setAlternateMobiles(updated);
  };

  const handleSaveProfile = async () => {
    if (!editName.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!editMobile || !/^\d{10}$/.test(editMobile)) {
      toast.error("Primary mobile number must be 10 digits");
      return;
    }

    // Validate alternate mobiles
    for (let i = 0; i < alternateMobiles.length; i++) {
      const mob = alternateMobiles[i];
      if (mob && !/^\d{10}$/.test(mob)) {
        toast.error(`Alternate mobile number ${i + 1} must be a valid 10-digit number`);
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append("name", editName.trim());
      formData.append("mobile_no", editMobile);
      formData.append("state", editState.trim());
      formData.append("vendor_id", vendor._id);
      
      const filteredAlts = alternateMobiles.filter(m => m.trim().length > 0);
      formData.append("alternate_mobile_numbers", JSON.stringify(filteredAlts));

      if (logoFile) {
        formData.append("company_logo", logoFile);
      }

      const response = await instance.post("/vendor/updateProfile", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status) {
        toast.success("Profile updated successfully");
        setVendor(response.data.data);
        setOpenEdit(false);
      } else {
        toast.error(response.data.msg || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.error || "An error occurred while updating profile");
    }
  };

  if (loading) {
    return (
      <div className="profile-page-wrapper">
        <Topbar />
        <div className="profile-loading">Loading Profile...</div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="profile-page-wrapper">
        <Topbar />
        <div className="profile-loading">No Profile Found</div>
      </div>
    );
  }

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((n) => n[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  return (
    <div className="profile-page-wrapper">
      <Topbar />
      
      <div className="profile-header-actions">
        <button className="profile-back-btn" onClick={() => navigate("/admin/Dashboard")}>
          Back
        </button>
      </div>

      <div className="profile-content-container">
        <Card className="profile-card-premium">
          <div className="profile-card-banner"></div>
          
          <CardContent className="profile-card-body">
            <div className="profile-avatar-container">
              {vendor.company_logo_url ? (
                <img src={vendor.company_logo_url} alt="Company Logo" className="profile-logo-img" />
              ) : (
                <div className="profile-logo-placeholder">
                  {getInitials(vendor.companyname || vendor.name)}
                </div>
              )}
            </div>

            <Typography className="profile-company-name" variant="h4">
              {vendor.companyname || "Company Profile"}
            </Typography>
            <Typography className="profile-vendor-role" variant="subtitle1">
              Vendor Partner
            </Typography>

            <Divider className="profile-divider" />

            <Grid container spacing={3} className="profile-details-grid">
              <Grid item xs={12} sm={6}>
                <div className="profile-info-item">
                  <div className="profile-info-icon-wrapper">
                    <FiUser className="profile-info-icon" />
                  </div>
                  <div className="profile-info-text">
                    <Typography className="profile-info-label">Contact Person</Typography>
                    <Typography className="profile-info-value">{vendor.name}</Typography>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <div className="profile-info-item">
                  <div className="profile-info-icon-wrapper">
                    <FiMail className="profile-info-icon" />
                  </div>
                  <div className="profile-info-text">
                    <Typography className="profile-info-label">Email Address</Typography>
                    <Typography className="profile-info-value">{vendor.email}</Typography>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <div className="profile-info-item">
                  <div className="profile-info-icon-wrapper">
                    <FiPhone className="profile-info-icon" />
                  </div>
                  <div className="profile-info-text">
                    <Typography className="profile-info-label">Primary Mobile</Typography>
                    <Typography className="profile-info-value">{vendor.mobile_no}</Typography>
                  </div>
                </div>
              </Grid>

              <Grid item xs={12} sm={6}>
                <div className="profile-info-item">
                  <div className="profile-info-icon-wrapper">
                    <FiMapPin className="profile-info-icon" />
                  </div>
                  <div className="profile-info-text">
                    <Typography className="profile-info-label">Operating State</Typography>
                    <Typography className="profile-info-value">{vendor.state || "--"}</Typography>
                  </div>
                </div>
              </Grid>
            </Grid>

            <Divider className="profile-divider" />

            <div className="profile-section-alternate">
              <Typography className="profile-section-title" variant="h6">
                Alternate Contact Numbers
              </Typography>
              
              <Grid container spacing={2} className="profile-alt-grid">
                {vendor.alternate_mobile_numbers && vendor.alternate_mobile_numbers.length > 0 ? (
                  vendor.alternate_mobile_numbers.map((num, idx) => (
                    <Grid item xs={12} sm={4} key={idx}>
                      <div className="profile-alt-phone-card">
                        <FiPhone className="profile-alt-icon" />
                        <Typography className="profile-alt-value">{num}</Typography>
                      </div>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Typography className="profile-alt-placeholder">
                      No alternate mobile numbers added. Edit profile to add up to 4 alternate contacts.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </div>

            <div className="profile-action-footer">
              <Button
                variant="contained"
                startIcon={<FiEdit />}
                onClick={handleOpenEdit}
                className="profile-edit-btn-premium"
              >
                Edit Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Edit Profile Dialog */}
      <Dialog open={openEdit} onClose={handleCloseEdit} maxWidth="sm" fullWidth className="profile-edit-dialog">
        <DialogTitle className="profile-dialog-title">
          <span>Edit Profile</span>
          <IconButton onClick={handleCloseEdit} size="small" className="profile-dialog-close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="profile-dialog-content">
          <Box className="profile-dialog-logo-section">
            <div className="profile-dialog-logo-preview-container">
              {logoPreview ? (
                <img src={logoPreview} alt="Logo Preview" className="profile-dialog-logo-preview" />
              ) : (
                <div className="profile-dialog-logo-preview-placeholder">
                  {getInitials(vendor.companyname || editName)}
                </div>
              )}
            </div>
            
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleLogoChange}
            />
            
            <Button
              variant="outlined"
              startIcon={<FiUploadCloud />}
              onClick={() => fileInputRef.current.click()}
              className="profile-dialog-upload-btn"
            >
              Upload Company Logo
            </Button>
          </Box>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Contact Name *"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Enter full name"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Primary Mobile *"
                value={editMobile}
                onChange={(e) => {
                  if (/^\d*$/.test(e.target.value) && e.target.value.length <= 10) {
                    setEditMobile(e.target.value);
                  }
                }}
                placeholder="10 digit number"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Operating State"
                value={editState}
                onChange={(e) => setEditState(e.target.value)}
                placeholder="e.g. Andhra Pradesh"
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <Box className="profile-dialog-alt-header">
                <Typography variant="subtitle2" className="profile-dialog-alt-title">
                  Alternate Mobile Numbers ({alternateMobiles.length}/4)
                </Typography>
                <Button
                  size="small"
                  startIcon={<FiPlus />}
                  onClick={handleAddAltMobile}
                  disabled={alternateMobiles.length >= 4}
                  className="profile-dialog-add-alt-btn"
                >
                  Add
                </Button>
              </Box>
              
              <Box className="profile-dialog-alt-list">
                {alternateMobiles.map((mob, idx) => (
                  <Box key={idx} className="profile-dialog-alt-row">
                    <TextField
                      fullWidth
                      label={`Alternate Mobile ${idx + 1}`}
                      value={mob}
                      onChange={(e) => handleAltMobileChange(idx, e.target.value)}
                      placeholder="10 digit number"
                      size="small"
                      error={mob.length > 0 && mob.length < 10}
                      helperText={mob.length > 0 && mob.length < 10 ? "Must be 10 digits" : ""}
                    />
                    <IconButton
                      onClick={() => handleRemoveAltMobile(idx)}
                      color="error"
                      size="small"
                      className="profile-dialog-alt-delete"
                    >
                      <FiTrash2 />
                    </IconButton>
                  </Box>
                ))}
                
                {alternateMobiles.length === 0 && (
                  <Typography variant="body2" className="profile-dialog-alt-none">
                    No alternate mobile numbers added yet.
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className="profile-dialog-actions">
          <Button onClick={handleCloseEdit} className="profile-dialog-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSaveProfile} className="profile-dialog-save-btn">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Profile;
